import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import { SettingsTooltip } from 'src/components/molecules';
import { useNotifications } from 'src/hooks/useNotifications';
import { getCurrentUser, logoutSuccessful } from 'src/reducers/Reporting.slice';
import { useTheme } from 'styled-components';
import { NavbarVariantEnum } from 'src/types';
import { getInitials } from 'src/utils/getInitials';
import * as Intercom from 'src/utils/intercom';
import { BaseNavbar } from './BaseNavbar';
import {
  AccountNavLink,
  AccountText,
  AccountTextContainer,
  LinkWithBadgeContainer,
  NavBarIconNotificationContainer,
  ProfileContainer,
  ReporterNavbarBottomMenu,
  StyledNavAvatar,
  StyledNavBarIcon,
  StyledNotificationBadge,
} from './BaseNavbar.styles';
import { useNavbarContext } from './NavbarProvider';
import { NavLink } from './components/NavLink';
import { NotificationsList } from './components/NotificationsList';

const NavMenu = () => {
  const { t } = useTranslation();
  const { isOpen } = useNavbarContext();

  return (
    <NavLink
      to="reports"
      data-testid="reports-navbar"
      icon={'uil uil-file-alt'}
      text={t('general.navbar.reporter.myReports')}
      navOpen={Boolean(isOpen)}
      tooltipText={t('general.navbar.reporter.myReports')}
      variant={NavbarVariantEnum.REPORTER}
    />
  );
};

const BottomMenu = () => {
  const { t } = useTranslation();
  const settingsTooltipRef = useRef<HTMLDivElement>(null);
  const [settingsTooltipRoot, setSettingsTooltipRoot] =
    useState<HTMLElement | null>(null);
  const [settingsTooltip, setSettingsTooltip] = useState(false);

  const { isOpen, isSecondaryDrawerOpen, setSecondaryDrawerOpen } =
    useNavbarContext();
  const { user: currentUser } = useSelector(getCurrentUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const theme = useTheme();
  const { numberOfNotifications, hasNotifications } = useNotifications();

  const onLogout = () => {
    dispatch(logoutSuccessful());
    navigate('/reporting/login', { replace: true });
  };

  return (
    <ReporterNavbarBottomMenu
      open={isOpen}
      variant={NavbarVariantEnum.REPORTER}
      responsive={true}
    >
      <AccountNavLink
        variant={NavbarVariantEnum.REPORTER}
        data-testid="settings-link-navbar-notifications"
        notificationOpen={isSecondaryDrawerOpen}
        onClick={() => setSecondaryDrawerOpen(!isSecondaryDrawerOpen)}
      >
        <LinkWithBadgeContainer open={isOpen} notification={hasNotifications}>
          <NavBarIconNotificationContainer
            open={isOpen}
            notification={hasNotifications}
          >
            <StyledNotificationBadge
              open={!isOpen}
              variant={'custom'}
              color={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N0
                  : theme.atlas.semantic.color.type.inverse
              }
              background-color={
                theme.isVaultVariant
                  ? theme.newPalette.turquoise.T300
                  : theme.atlas.core.color.brand.diligentRed
              }
            />
            <StyledNavBarIcon className="uil uil-bell" size="20px" />
          </NavBarIconNotificationContainer>
          <AccountText open={isOpen}>
            {t('general.navbar.notifications')}
          </AccountText>

          {hasNotifications && (
            <StyledNotificationBadge
              open={isOpen}
              data-testid="notifications-badge-count"
              variant={'custom'}
              text={numberOfNotifications?.toString()}
              color={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N0
                  : theme.atlas.semantic.color.type.inverse
              }
              background-color={
                theme.isVaultVariant
                  ? theme.newPalette.turquoise.T300
                  : theme.atlas.core.color.brand.diligentRed
              }
            />
          )}
        </LinkWithBadgeContainer>
      </AccountNavLink>

      <AccountNavLink
        variant={NavbarVariantEnum.REPORTER}
        data-testid="settings-link-navbar-technical-support"
        onClick={() => (Intercom.isOpen ? Intercom.hide() : Intercom.show())}
      >
        <StyledNavBarIcon className="uil uil-chat" size="20px" />
        <AccountText open={isOpen}>
          {t('general.navbar.technicalSupport')}
        </AccountText>
      </AccountNavLink>

      <div ref={settingsTooltipRef}>
        <ProfileContainer
          variant={NavbarVariantEnum.REPORTER}
          settingsTooltip={settingsTooltip}
          ref={setSettingsTooltipRoot}
          onClick={() => setSettingsTooltip(!settingsTooltip)}
          data-testid="account-button"
        >
          <StyledNavAvatar
            dark={false}
            key={currentUser?.name}
            variant="text"
            text={getInitials(currentUser?.name)}
            size="small"
            textColor={
              theme.isVaultVariant
                ? theme.newPalette.turquoise.T300
                : theme.atlas.semantic.color.action.secondaryInverse
                    .onSecondaryInverse
            }
          />
          {isOpen && (
            <AccountTextContainer open={isOpen}>
              <AccountText open={isOpen}>{currentUser?.name}</AccountText>
              <StyledNavBarIcon
                className="uil uil-ellipsis-v"
                size="20px"
                color={
                  theme.isVaultVariant
                    ? theme.newPalette.turquoise.T300
                    : theme.atlas.semantic.color.action.secondaryInverse
                        .onSecondaryInverse
                }
              />
            </AccountTextContainer>
          )}
        </ProfileContainer>
        {settingsTooltip && (
          <SettingsTooltip
            root={settingsTooltipRoot as HTMLElement}
            closeTooltip={() => setSettingsTooltip(false)}
            variant={NavbarVariantEnum.REPORTER}
            onLogout={onLogout}
          />
        )}
      </div>
    </ReporterNavbarBottomMenu>
  );
};

export const ReportingNavbar = () => {
  const { notifications } = useNotifications();
  return (
    <BaseNavbar
      variant={NavbarVariantEnum.REPORTER}
      topNavList={<NavMenu />}
      topHeader="Reporter Hub"
      bottomNavList={<BottomMenu />}
      secondaryDrawer={
        <NotificationsList
          notifications={notifications}
          variant={NavbarVariantEnum.REPORTER}
        />
      }
    />
  );
};
